import {
  getFirstPromotion,
  getNormalizedQuantity,
  normalizeNutritionalDetails,
  normalizeNutritionalWarnings,
  PRODUCT_TYPES,
} from '@/features/shared/utils/normalizeProduct'
import {
  type ProductCampaignsPromotion,
  type Product,
} from '@/features/shop/services/Product/types'
import { getFragmentData } from '@/gql'
import {
  type PdpProductQuery,
  type ProductFieldsFragment,
  ProductType,
  ProductFieldsFragmentDoc,
} from '@/gql/graphql'
import { isObject } from '@/features/shared/utils/isObject'

type PdpProductGql = NonNullable<PdpProductQuery['product']>

export function mapFromGqlProductToProduct(fragmentedProduct: PdpProductGql): Product {
  const gqlProduct = getFragmentData(ProductFieldsFragmentDoc, fragmentedProduct)
  const normalizedQuantity = getNormalizedQuantity({
    product_type: mapProductType(gqlProduct.productType),
    quantity: 0, // this property only relates to ActiveOrders.
    has_custom_label: gqlProduct.hasCustomLabel,
    unit_weight: gqlProduct.unitWeight,
  })
  const prices = priceMapping(gqlProduct, normalizedQuantity)
  const productVariationInfoProperties = mapVariationInfoProperties(fragmentedProduct)
  const parentCouponId = gqlProduct.coupons?.[0]?.id ?? ''
  const nutritionalDetails = isObject(gqlProduct.nutritionalDetails)
    ? gqlProduct.nutritionalDetails
    : null
  return {
    couponId: parentCouponId,
    hierarchical_categories: undefined, // only needed for highest category id
    line_item_promotion: {},
    ...productVariationInfoProperties,
    variationProducts: fragmentedProduct.variationInfo?.variantProducts
      ? fragmentedProduct.variationInfo.variantProducts
          .filter((p) => !!p)
          .map((p) => {
            const pf = getFragmentData(ProductFieldsFragmentDoc, p)
            if (!pf.variationInfo) {
              return undefined
            }
            const mapped = mapFromGqlProductToProduct({
              ...pf,
              variationInfo: {
                ...pf.variationInfo, // ensure we pass through variation info to nested variations
                variantProducts: null,
              },
            })
            mapped.purchaseLimit = gqlProduct.purchaseLimit ?? 0
            mapped.couponId = parentCouponId
            return mapped
          })
          .filter((p) => !!p)
      : [],
    // this only applies to active orders, not PDP. I think this is fine? We'll need to test how active orders works with these GQL changes
    quantity: normalizedQuantity,
    nutrition_attributes_for_you:
      gqlProduct.memberPreferenceData?.nutritionalAttributesForYou?.map((n) => ({
        tag: n.tag,
        ...(n.description ? { description: n.description } : {}),
      })) ?? [],
    for_you: gqlProduct.memberPreferenceData?.forYou ?? false,

    // END NEED WIP PROPERTIES

    id: Number(gqlProduct.id),
    rootCategoryId: Number(gqlProduct.rootCryptexCategory?.id ?? 0),
    // we will not be getting note from gql as part of this initial cutover - https://app.clickup.com/t/86b6reh2u
    note: '',
    name: gqlProduct.displayName,
    bogo: !!gqlProduct.bogo, // does this need to take into account `onSale`
    size: gqlProduct.size, // size in prism PDP is non-nullable
    hasCustomLabel: !!gqlProduct.hasCustomLabel,
    storeLocationId: Number(gqlProduct.storeLocationId),
    storeId: Number(gqlProduct.retailerId),
    purchaseLimit: gqlProduct.purchaseLimit ?? 0,
    bulletPoints: gqlProduct.bulletPoints ?? [],
    allergenInfo: gqlProduct.allergenInfo,
    // should this come from categories, just in case?
    hasAlcoholTerms: gqlProduct.hasAlcoholTerms,
    image: {
      // primary is non-nullable, but there is logic in ProductImageCarousel that assumes original_size_url is nullable and therefore falls back to url
      original_size_url: gqlProduct.images.primary,
      url: gqlProduct.images.thumbnail,
    },
    secondaryImages: gqlProduct.images.secondary ?? [],
    review_stats: {
      average_rating: gqlProduct.stats?.averageRating ?? 0,
      rating_distribution: gqlProduct.stats?.ratingDistribution
        ? {
            1: gqlProduct.stats.ratingDistribution.rating1,
            2: gqlProduct.stats.ratingDistribution.rating2,
            3: gqlProduct.stats.ratingDistribution.rating3,
            4: gqlProduct.stats.ratingDistribution.rating4,
            5: gqlProduct.stats.ratingDistribution.rating5,
          }
        : null,
      total_reviews: gqlProduct.stats?.totalReviews ?? 0,
      recommended_percentage: gqlProduct.stats?.recommendedPercentage ?? 0,
      total_ratings: gqlProduct.stats?.totalRatings ?? 0,
    },
    brandName: gqlProduct.brandName ?? '',
    type: mapProductType(gqlProduct.productType),
    description: gqlProduct.description,
    price: gqlProduct.price ?? 0,
    salePrice: gqlProduct.salePrice ?? 0,
    original_price: gqlProduct.originalPrice ?? 0,
    totalPrice: prices.totalPrice,
    totalOriginalPrice: prices.totalOriginalPrice,
    totalSalePrice: prices.totalSalePrice,
    unitLabel: prices.unitLabel,
    pricing: {
      loyalty: {
        price: gqlProduct.pricing?.loyalty?.price ?? 0,
        startsOn: gqlProduct.pricing?.loyalty?.startsOn ?? null,
        endsOn: gqlProduct.pricing?.loyalty?.endsOn ?? null,
      },
    },
    oos: gqlProduct.outOfStock,
    lowStock: gqlProduct.lowStock,
    onSale: gqlProduct.onSale ?? false,

    unitWeight: gqlProduct.unitWeight ?? 0,
    unitOfMeasure: gqlProduct.unitOfMeasure ?? '',

    bought: !!gqlProduct.bought,
    is_snap_ebt_eligible: gqlProduct.isSnapEbtEligible,

    isCustomProduct: gqlProduct.isCustomProduct,
    available: !!gqlProduct.available,
    descriptionLabels: gqlProduct.descriptionLabels ?? [],
    universal_id: gqlProduct.universalId,
    chokingHazard: gqlProduct.chokingHazard ?? '',
    ingredients: gqlProduct.ingredients,
    nmu_eligible: gqlProduct.nmuEligible, // should this go on store setting?
    nutritionalDetails: normalizeNutritionalDetails(nutritionalDetails),
    nutritionalWarnings: normalizeNutritionalWarnings(nutritionalDetails),
    cpgPromotion: mapPromo(gqlProduct.promotions),

    // PROPERTIES NOT USED ON PDP
    search_boost_aliases: [], // not applicable to PDP
    beacons: {}, // deliberately empty here as beacons don't apply to PDP. Will be included at a later date
    parent: undefined, // specific to search, will be removed soon
    variationParent: undefined, // this isnt used in code aside from one check for logging...
    featured: false,
    // not used https://shipt.slack.com/archives/C041LNQT4SK/p1755713614598859?thread_ts=1755638087.306089&cid=C041LNQT4SK
    featuredSource: undefined,
    featuredTransactionId: undefined,
    // END PROPERTIES NOT USED ON PDP
    highlights: gqlProduct.highlights ?? [],
    specifications: gqlProduct.specifications ?? [],
    originalSubtotal: 0,
  }
}

function mapVariationInfoProperties(gqlProduct: PdpProductGql) {
  return {
    // maintain legacy behavior of null parentProductId if no variationInfo
    parentProductId: gqlProduct.variationInfo?.parentProductId
      ? Number(gqlProduct.variationInfo.parentProductId)
      : null,
    variationDisplayName: gqlProduct.variationInfo?.displayName ?? '',
    // legacy returns undefined if color is empty. We need to mimic this in mapping
    variationColor: gqlProduct.variationInfo?.colorOption?.color
      ? gqlProduct.variationInfo.colorOption.color
      : undefined,
    variationSize: gqlProduct.variationInfo?.sizeOption?.size,
    swatchUrl: gqlProduct.variationInfo?.colorOption?.swatchUrl ?? '',
    variationProperties: gqlProduct.variationInfo?.variationProperties,
  }
}

export function getPropertiesFromColorAndSize(
  product: Pick<Product, 'variationColor' | 'variationSize' | 'swatchUrl'>,
) {
  const properties = [
    ...(product.variationColor
      ? [
          {
            name: 'Color',
            value: product.variationColor,
            swatchUrl: product.swatchUrl || null,
          },
        ]
      : []),
    ...(product.variationSize
      ? [{ name: 'Size', value: product.variationSize, swatchUrl: null }]
      : []),
  ]
  return properties.length > 0 ? properties : null
}

function mapProductType(productType: ProductType) {
  switch (productType) {
    case ProductType.ByWeight:
      return PRODUCT_TYPES.BY_WEIGHT
    case ProductType.Normal:
      return PRODUCT_TYPES.NORMAL
    default:
      return ''
  }
}

function mapPromo(
  gqlPromotions: ProductFieldsFragment['promotions'],
): ProductCampaignsPromotion | null {
  if (!gqlPromotions || gqlPromotions.length === 0) {
    return null
  }
  const mappedPromos = gqlPromotions.map((promo) => ({
    category_id: Number(promo.content?.categoryId ?? 0),
    category_name: promo.content?.productCallout ?? '',
    display_title: promo.content?.productCallout ?? '',
    id: Number(promo.id),
    start_date: '',
  }))
  return getFirstPromotion(mappedPromos)
}
type PriceMappingInput = {
  price: number | null
  salePrice: number | null
  originalPrice: number | null
  unitWeight: number | null
  productType: ProductType
  hasCustomLabel: boolean | null
  descriptionLabels: Array<string> | null
}

function priceMapping(
  product: PriceMappingInput,
  normalizedProductQuantity: number,
): {
  totalPrice: number
  totalSalePrice: number
  unitLabel: string
  totalOriginalPrice: number
} {
  const { price, salePrice, originalPrice, unitWeight } = product
  const descriptionLabels = product.descriptionLabels ?? []
  let totalPrice = price ?? 0
  let totalSalePrice = salePrice ?? 0
  let unitLabel = ''
  let totalOriginalPrice = originalPrice ?? 0

  if (product.productType === ProductType.ByWeight) {
    if (product.hasCustomLabel) {
      // Weighted products with a custom label are dynamic in their total cost
      // (think of pre-packaged meats that vary in cost due to their weight). The
      // total price of these products needs to be based on the product's unit weight.
      // The original_price field only appears when the user is c360 and the product has a markup. Future projects may extend this to all users. Original_price is just what FE would have received as the price before the no markup work.

      totalPrice = (price ?? 0) * (unitWeight ?? 0)
      totalSalePrice = (salePrice ?? 0) * (unitWeight ?? 0)
      totalOriginalPrice = (originalPrice ?? 0) * (unitWeight ?? 0)
    }

    // We need to check for "<= 1" (not just "=== 1") due to the fact that
    // "by weight" products without a custom label have a half-unit step,
    // which means they could have a "0.5" unit of measure quantity.
    //
    // All products seem to come back with description labels, but we only
    // want to use those custom labels if the product is "by weight".
    if (normalizedProductQuantity <= 1) {
      unitLabel = descriptionLabels[0] ?? '' // singular custom label
    } else {
      unitLabel = descriptionLabels[1] ?? '' // plural custom label
    }
  }
  return { totalPrice, totalSalePrice, totalOriginalPrice, unitLabel }
}

// Export for testing
export { mapProductType, mapPromo, priceMapping }
